import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/v2/components/ui/button";
import { tenantHoverClassName } from "@/v2/components/tenant-hover";
import { ClipRow, type ClipListItem } from "./ClipRow";
import type { DownloadRecord } from "../download";

export function ClipListSidebar({
  videoList,
  pageData,
  isSkeletonList,
  activeClip,
  onSelectClip,
  handlePrevious,
  handleNext,
  allDownloadVideo,
  isDownloadAll,
  setIsDownloadAll,
  subscriptionDetails,
  userDetails,
  onDownloadRequested,
}: {
  videoList: ClipListItem[];
  pageData: any;
  isSkeletonList: boolean;
  activeClip: string | number;
  onSelectClip: (clip: ClipListItem, index: number) => void;
  handlePrevious: () => void;
  handleNext: () => void;
  allDownloadVideo: DownloadRecord[];
  isDownloadAll: boolean;
  setIsDownloadAll: (value: boolean) => void;
  subscriptionDetails: any;
  userDetails: any;
  onDownloadRequested?: () => void;
}) {
  return (
    // h-full + a flex-1 scroll area makes the rail end level with the player
    // card beside it: the row is a stretch flex container, so this column's
    // height is already the player's, and the clip list absorbs whatever is
    // left under the two fixed bars. Below lg the row stacks and that height
    // is content-driven, so the list keeps a cap of its own there.
    <div className="rounded-xl border border-slate-200 bg-card flex flex-col min-h-0 h-full">
      <div
        className="flex items-center justify-between border-b border-slate-100"
        style={{ padding: "12px 16px" }}
      >
        <h3 className="font-bold text-slate-900 text-sm">In this Spotlight</h3>
        {/* pageData.total is every clip the channel's filters match, which is
            what "N clips" should mean. The endpoint stops counting at 2,000 and
            sets totalCapped, so past that this says "2,000+" rather than
            claiming the cap is the real number. Falls back to this page's
            length for a response with no total (the endpoint only returns one
            when asked, and older deployments do not return it at all). */}
        <span className="text-xs font-semibold text-slate-500">
          {(pageData?.total ?? videoList?.length ?? 0).toLocaleString()}
          {pageData?.totalCapped ? "+" : ""} clips
        </span>
      </div>

      <div
        className="flex items-center justify-between border-b border-slate-100"
        style={{ padding: "8px 12px", gap: "8px" }}
      >
        <Button
          variant="outline"
          size="sm"
          disabled={pageData?.isFirstPage || isSkeletonList}
          onClick={handlePrevious}
          className={`gap-1 ${tenantHoverClassName}`}
        >
          <ChevronLeft className="w-4 h-4" /> Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={pageData?.isLastPage || isSkeletonList}
          onClick={handleNext}
          className={`gap-1 ${tenantHoverClassName}`}
        >
          Next <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      <div
        className="overflow-y-auto flex-1 min-h-0 max-h-[560px] lg:max-h-none"
        style={{ padding: "8px" }}
      >
        {isSkeletonList ? (
          /* The Audience Layer's loading pattern (ConversationFeed): static
             --v2-muted bars at the radius token, announced once through a
             role="status" wrapper with the bars themselves aria-hidden. Not
             the shadcn Skeleton, which pulses in accent blue. Bars are the
             height of a real clip row (64px thumbnail plus its padding). */
          <div className="flex flex-col gap-1" role="status">
            <span className="sr-only">Loading clips</span>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                aria-hidden="true"
                style={{
                  height: 80,
                  borderRadius: "var(--v2-radius)",
                  background: "var(--v2-muted)",
                }}
              />
            ))}
          </div>
        ) : videoList?.length > 0 ? (
          <div className="flex flex-col gap-1">
            {videoList.map((clip, index) => (
              <ClipRow
                key={clip.game_event_id}
                clip={clip}
                active={String(activeClip) === String(clip.game_event_id)}
                onSelect={() => onSelectClip(clip, index)}
                allDownloadVideo={allDownloadVideo}
                isDownloadAll={isDownloadAll}
                setIsDownloadAll={setIsDownloadAll}
                subscriptionDetails={subscriptionDetails}
                userDetails={userDetails}
                onDownloadRequested={onDownloadRequested}
              />
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate-500 text-center" style={{ padding: "24px 0" }}>
            There are currently no highlights for this game.
          </div>
        )}
      </div>
    </div>
  );
}
